import React, { useState } from "react";
import { Clock, MapPin, MessageCircle, Phone, ShieldCheck } from "lucide-react";
import {
  buildWhatsAppUrl,
  formatPhoneDisplay,
  getWhatsAppNumber,
} from "../../lib/company";
import { CompanyLogo } from "../ui/CompanyLogo";

/**
 * Rodapé (layout docs/tela): marca, contato e horário de funcionamento.
 * Usa somente os dados da empresa (GET /companies/{code}); não há fallback com dados de
 * outra loja. Enquanto a empresa não carrega, o rodapé não é exibido; campos não
 * cadastrados no painel simplesmente não aparecem.
 *
 * @param {Object|null} company - Dados da empresa (useCompany)
 * @param {string} companyName, description, phone, deliveryInfo - Fallbacks
 * @param {string[]} businessHours - Fallback dos horários
 */
export const Footer = ({
  company = null,
  companyName = "Demo MEI",
  description = "Informações e descrição.",
  phone = "(11) 9 9188-2938",
  deliveryInfo = "Delivery e Retirada",
  businessHours = [
    "Segunda a Sexta: 9h às 22h",
    "Sábado e Domingo: 10h às 23h",
  ],
}) => {
  const name = company?.name || companyName;
  const about = company?.description || description;
  const phoneLabel = company?.contactPhone
    ? formatPhoneDisplay(company.contactPhone)
    : "";
  const delivery =
    company?.delivery === false ? "Somente retirada" : "Delivery e retirada";
  const hours = [
    { label: "Dias de semana", value: company?.businessHoursWeekdays },
    { label: "Fim de semana", value: company?.businessHoursWeekend },
  ].filter((h) => h.value);
  const whatsappNumber = getWhatsAppNumber(company);
  const whatsappUrl = whatsappNumber && buildWhatsAppUrl(whatsappNumber);
  const telDigits = String(phoneLabel).replace(/\D/g, "");

  return (
    <footer className="bg-warm-950 text-stone-300 mt-12 sm:mt-16 pt-12 sm:pt-14 pb-10 border-t border-warm-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-8">
          {/* Marca */}
          <div className="sm:col-span-2 md:col-span-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white p-1 flex items-center justify-center flex-shrink-0">
                <CompanyLogo
                  company={company}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-serif text-2xl font-bold text-white tracking-tight">
                {name}
              </span>
            </div>
            {about && (
              <p className="text-sm text-stone-400 leading-relaxed max-w-sm">
                {about}
              </p>
            )}
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Falar no WhatsApp"
                className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-warm-900 hover:bg-emerald-600 hover:text-white border border-warm-800 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
          </div>

          {/* Contato */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-white">
              Contato
            </h4>
            <ul className="space-y-2.5 text-sm text-stone-400">
              {phoneLabel && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  <a
                    href={`tel:${telDigits}`}
                    className="inline-flex items-center min-h-[44px] -my-3 hover:text-white transition-colors"
                  >
                    {phoneLabel}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brand-500 flex-shrink-0" />
                <span>{delivery}</span>
              </li>
            </ul>
          </div>

          {/* Horário */}
          {hours.length > 0 && (
            <div className="md:col-span-4 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-500" />
                Horário de funcionamento
              </h4>
              <div className="text-sm bg-warm-900/60 p-4 rounded-2xl border border-warm-800 divide-y divide-warm-800">
                {hours.map((h, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="text-stone-400">{h.label}</span>
                    <span className="text-white font-semibold text-right">
                      {h.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {company?.footerInfo && (
          <p className="text-xs text-stone-500 leading-relaxed">
            {company.footerInfo}
          </p>
        )}

        <div className="pt-6 border-t border-warm-900 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500 text-center sm:text-left">
          <p>
            © {new Date().getFullYear()} {name}. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-emerald-500 font-semibold inline-flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Pagamento seguro
            </span>
            <span className="bg-warm-900 px-3 py-1 rounded-full text-stone-400 text-[11px]">
              v{process.env.REACT_APP_VERSION || "1.0.0"}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
